import { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { getCampaniaGestionCartera, getCampaniaEstadoSelect, editarEstadoCampania } from '../api/apiConfig';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { MdReceipt, MdArrowForward } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const ESTADO_CLIENTE_OPERANDO = 'Cliente operando';
const UMBRAL_VOLUMEN_BAJO = 5_000_000;

const Campanas = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [estadoOptions, setEstadoOptions] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' o 'desc'
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 15;

  const clienteOperaEnMes = (item) => (Number(item.ANTICIPADO_IF) || 0) > 0;
  const isEstadoBloqueado = (item) => item.BLOQUEADO_ESTADO === true || clienteOperaEnMes(item);

  const getEstadoOptionsForRow = (item) =>
    estadoOptions.filter(
      (opt) => opt.NOM_COD_CORTO !== ESTADO_CLIENTE_OPERANDO || clienteOperaEnMes(item)
    );

  const getEffectiveEstado = (item) => {
    if (clienteOperaEnMes(item)) return ESTADO_CLIENTE_OPERANDO;
    if (item.ESTADO === ESTADO_CLIENTE_OPERANDO) return 'Sin gestión';
    return item.ESTADO || '';
  };

  const getTooltipOperando = (item) => {
    const monto = Number(item.ANTICIPADO_IF) || 0;
    const montoFmt = `$${monto.toLocaleString('es-CL')}`;
    if (monto < UMBRAL_VOLUMEN_BAJO) {
      return `Operado en el mes: ${montoFmt}. Volumen muy bajo (menor a $5.000.000).`;
    }
    return `Operado en el mes: ${montoFmt}`;
  };

  const getRowBackgroundColor = (item) => {
    if (isEstadoBloqueado(item)) return '#f0fdf4';
    if (getEffectiveEstado(item) === 'Sin gestión') return '#fee2e2';
    return 'transparent';
  };

  const handleEstadoChange = async (item, nuevoEstado) => {
    if (isEstadoBloqueado(item)) return;

    const idCam = item.ID_CAM;
    console.log('Updating estado:', idCam, nuevoEstado);
    try {
      await editarEstadoCampania(idCam, nuevoEstado, item.RUT);
      console.log('API call successful, updating local state');
      setData(prevData => {
        const updatedData = prevData.map(row => {
          if (row.ID_CAM === idCam && row.RUT === item.RUT) {
            console.log('Found item to update:', row.ID_CAM, 'new estado:', nuevoEstado);
            return { ...row, ESTADO: nuevoEstado };
          }
          return row;
        });
        console.log('Updated data:', updatedData);
        return updatedData;
      });
    } catch (error) {
      console.error('Error updating estado:', error);
      alert(error.message || 'No se pudo actualizar el estado');
    }
  };

  const filterData = (dataToFilter) => {
    if (!searchTerm.trim()) return dataToFilter;

    const searchLower = searchTerm.toLowerCase().trim();
    return dataToFilter.filter(item => {
      // Buscar en todas las columnas relevantes
      const searchableFields = [
        item.RUT,
        item.CLIENTE,
        item.NOMBRE_CAM,
        item.OFERTA,
        item.PENETRACION,
        item.EJECUTIVO,
        item.EQUIPO,
        item.RESPONSABLE_GESTION,
        item.ESTADO,
        item.ANTICIPADO_IF?.toString(),
        item.CESIONES_OTROS?.toString()
      ];

      return searchableFields.some(field => {
        if (field == null) return false;
        return String(field).toLowerCase().includes(searchLower);
      });
    });
  };

  const getSortedData = () => {
    let dataToSort = filterData(data);
    
    if (!sortColumn) return dataToSort;

    return [...dataToSort].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      // Manejar valores nulos o indefinidos
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Determinar si es un número
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);

      let comparison = 0;

      if (isNumeric) {
        // Ordenamiento numérico
        comparison = aNum - bNum;
      } else {
        // Ordenamiento alfabético
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        comparison = aStr.localeCompare(bStr, 'es', { numeric: true, sensitivity: 'base' });
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const exportToExcel = () => {
    const dataToExport = getSortedData();
    const exportData = dataToExport.map(item => ({
      'RUT': item.RUT,
      'Cliente': item.CLIENTE,
      'Campaña': item.NOMBRE_CAM,
      'Oferta': item.OFERTA,
      'Penetración': item.PENETRACION,
      'Ejecutivo': item.EJECUTIVO,
      'Ejecutivo líneas (hoy)': item.EJECUTIVO_LINEAS || '',
      'Equipo': item.EQUIPO,
      'Responsable Gestión': item.RESPONSABLE_GESTION,
      'Anticipado IF': item.ANTICIPADO_IF?.toFixed(0).replace('.', ',') || '0',
      'Cesiones Otros': item.CESIONES_OTROS?.toFixed(0).replace('.', ',') || '0',
      'Estado': getEffectiveEstado(item)
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Campañas');
    
    XLSX.writeFile(wb, `campanas_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Cargar datos de campaña
        const response = await getCampaniaGestionCartera(user);
        let parsedData = response;
        if (response && response.body && typeof response.body === 'string') {
          parsedData = JSON.parse(response.body);
        }
        console.log('Initial data loaded:', parsedData);
        setData(parsedData || []);
        
        // Cargar opciones de estado
        const estadoResponse = await getCampaniaEstadoSelect();
        let estadoData = estadoResponse;
        if (estadoResponse && estadoResponse.body && typeof estadoResponse.body === 'string') {
          estadoData = JSON.parse(estadoResponse.body);
        }
        setEstadoOptions(estadoData || []);
        
      } catch (err) {
        setError('Error al cargar los datos');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Si ya está ordenando por esta columna, cambiar la dirección
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Si es una nueva columna, ordenar ascendente
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Resetear a la primera página al ordenar
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Resetear a la primera página al buscar
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const sortedData = getSortedData();
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    }
    return sortDirection === 'asc' 
      ? <FaSortUp style={{ marginLeft: '4px' }} /> 
      : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const maxButtons = 7;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxButtons - 1);
    
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-baseline gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Campañas</h1>
        </div>
        <div className="flex justify-center items-center h-32">
          <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-baseline gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Campañas</h1>
        </div>
        <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Campañas</h1>
        {data.length > 0 && (
          <button
            onClick={exportToExcel}
            className="btn-primary"
            style={{
              background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              borderLeft: '3px solid var(--if-primary)'
            }}
          >
            <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
            Exportar Excel
          </button>
        )}
      </div>

      {/* Barra de búsqueda */}
      {data.length > 0 && (
        <div className="mb-4">
          <div className="relative" style={{ maxWidth: '500px' }}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Buscar en todas las columnas..."
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{
                fontSize: '0.875rem',
                backgroundColor: 'white',
                transition: 'all 0.2s ease'
              }}
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center hover:opacity-70 transition-opacity"
                style={{ color: '#9ca3af' }}
                title="Limpiar búsqueda"
              >
                <IoClose className="h-5 w-5" />
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="mt-2 text-sm" style={{ color: 'var(--if-text-muted)' }}>
              {sortedData.length === 0 ? (
                <span>No se encontraron resultados para "{searchTerm}"</span>
              ) : (
                <span>
                  {sortedData.length} {sortedData.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}
                  {sortedData.length !== data.length && ` de ${data.length} registros`}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      
      <div className="seccion-datos">
        {data.length === 0 ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
            No hay datos disponibles
          </div>
        ) : sortedData.length === 0 && searchTerm ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
            No se encontraron resultados para "{searchTerm}"
          </div>
        ) : (
          <div className="tabla-container" style={{maxHeight: '500px', overflowY: 'auto', overflowX: 'auto'}}>
            <table style={{fontSize: '0.875rem', tableLayout: 'fixed', width: '100%'}}>
              <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>
                <tr>
                  <th 
                    style={{width: '90px', cursor: 'pointer', userSelect: 'none'}}
                    onClick={() => handleSort('RUT')}
                  >
                    RUT {getSortIcon('RUT')}
                  </th>
                  <th 
                    style={{width: '250px', cursor: 'pointer', userSelect: 'none'}}
                    onClick={() => handleSort('CLIENTE')}
                  >
                    Cliente {getSortIcon('CLIENTE')}
                  </th>
                  <th 
                    style={{width: '150px', cursor: 'pointer', userSelect: 'none'}}
                    onClick={() => handleSort('NOMBRE_CAM')}
                  >
                    Campaña {getSortIcon('NOMBRE_CAM')}
                  </th>
                  <th 
                    style={{width: '200px', cursor: 'pointer', userSelect: 'none'}}
                    onClick={() => handleSort('OFERTA')}
                  >
                    Oferta {getSortIcon('OFERTA')}
                  </th>
                  <th 
                    style={{width: '150px', cursor: 'pointer', userSelect: 'none'}}
                    onClick={() => handleSort('PENETRACION')}
                  >
                    Penetración {getSortIcon('PENETRACION')}
                  </th>
                  <th 
                    style={{width: '120px', cursor: 'pointer', userSelect: 'none'}}
                    onClick={() => handleSort('EJECUTIVO')}
                  >
                    Ejecutivo {getSortIcon('EJECUTIVO')}
                  </th>
                  <th 
                    style={{width: '80px', cursor: 'pointer', userSelect: 'none'}}
                    onClick={() => handleSort('EQUIPO')}
                  >
                    Equipo {getSortIcon('EQUIPO')}
                  </th>
                  <th 
                    style={{width: '120px', cursor: 'pointer', userSelect: 'none'}}
                    onClick={() => handleSort('RESPONSABLE_GESTION')}
                  >
                    Responsable {getSortIcon('RESPONSABLE_GESTION')}
                  </th>
                  <th 
                    style={{textAlign: 'right', width: '120px', cursor: 'pointer', userSelect: 'none'}}
                    onClick={() => handleSort('ANTICIPADO_IF')}
                  >
                    Volumen IF mes actual {getSortIcon('ANTICIPADO_IF')}
                  </th>
                  <th 
                    style={{textAlign: 'right', width: '120px', cursor: 'pointer', userSelect: 'none'}}
                    onClick={() => handleSort('CESIONES_OTROS')}
                  >
                    Cesiones Otros mes actual {getSortIcon('CESIONES_OTROS')}
                  </th>
                  <th 
                    style={{width: '120px', cursor: 'pointer', userSelect: 'none'}}
                    onClick={() => handleSort('ESTADO')}
                  >
                    Estado {getSortIcon('ESTADO')}
                  </th>
                  <th style={{width: '80px', textAlign: 'center'}}>Detalle Cliente</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, index) => (
                  <tr key={index} style={{backgroundColor: getRowBackgroundColor(item)}}>
                    <td>{item.RUT}</td>
                    <td>{item.CLIENTE}</td>
                    <td>{item.NOMBRE_CAM}</td>
                    <td>{item.OFERTA}</td>
                    <td>{item.PENETRACION}</td>
                    <td>
                      {item.EJECUTIVO}
                      {item.EJECUTIVO_LINEAS
                        && String(item.EJECUTIVO_LINEAS).trim()
                        && String(item.EJECUTIVO_LINEAS).trim() !== String(item.EJECUTIVO || '').trim()
                        ? (
                          <div
                            style={{ fontSize: '0.7rem', color: '#92400e', marginTop: 2 }}
                            title="Ejecutivo actual en sf_lineas (informativo; la bandeja usa el de la campaña)"
                          >
                            Líneas: {item.EJECUTIVO_LINEAS}
                          </div>
                        )
                        : null}
                    </td>
                    <td>{item.EQUIPO}</td>
                    <td>{item.RESPONSABLE_GESTION}</td>
                    <td style={{textAlign: 'right'}}>
                      ${item.ANTICIPADO_IF?.toLocaleString('de-DE') || '0'}
                    </td>
                    <td style={{textAlign: 'right'}}>
                      ${item.CESIONES_OTROS?.toLocaleString('de-DE') || '0'}
                    </td>
                    <td>
                      <span
                        title={isEstadoBloqueado(item) ? getTooltipOperando(item) : undefined}
                        style={{
                          display: 'block',
                          cursor: isEstadoBloqueado(item) ? 'not-allowed' : 'default',
                        }}
                      >
                        <select
                          value={getEffectiveEstado(item)}
                          onChange={(e) => {
                            console.log('Select changed for item:', item.ID_CAM, 'to:', e.target.value);
                            handleEstadoChange(item, e.target.value);
                          }}
                          disabled={isEstadoBloqueado(item)}
                          className="w-full px-2 py-1 text-xs border rounded"
                          style={{
                            color: item.ESTADO === 'Sin gestión' && !isEstadoBloqueado(item) ? '#dc2626' : 'inherit',
                            backgroundColor: isEstadoBloqueado(item) && (Number(item.ANTICIPADO_IF) || 0) < UMBRAL_VOLUMEN_BAJO
                              ? '#fef3c7'
                              : 'white',
                            borderColor: isEstadoBloqueado(item) && (Number(item.ANTICIPADO_IF) || 0) < UMBRAL_VOLUMEN_BAJO
                              ? '#f59e0b'
                              : undefined,
                            cursor: isEstadoBloqueado(item) ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {!getEstadoOptionsForRow(item).find(opt => opt.NOM_COD_CORTO === getEffectiveEstado(item)) && getEffectiveEstado(item) && (
                            <option value={getEffectiveEstado(item)}>{getEffectiveEstado(item)}</option>
                          )}
                          {getEstadoOptionsForRow(item).map((option, idx) => (
                            <option key={idx} value={option.NOM_COD_CORTO}>
                              {option.NOM_COD_CORTO}
                            </option>
                          ))}
                        </select>
                      </span>
                    </td>
                    <td style={{textAlign: 'center'}}>
                      <button
                        onClick={() => navigate(`/detalle-cliente/${item.RUT}`)}
                        className="btn-primary"
                        style={{
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.75rem',
                          borderRadius: '4px',
                          width: '32px',
                          height: '32px'
                        }}
                        title="Ver detalle cliente"
                      >
                        <MdArrowForward className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Paginación */}
        {sortedData.length > itemsPerPage && (
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm" style={{color: 'var(--if-text-muted)'}}>
              Mostrando {startIndex + 1} a {Math.min(endIndex, sortedData.length)} de {sortedData.length} registros
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Anterior
              </button>
              {getPageNumbers().map(page => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`px-3 py-1 text-sm border rounded ${
                    currentPage === page ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Campanas;